import {
  PrivacyRequestSchema,
  PublicOrgListSchema,
  ProfileFormSchema,
  PublicOrgSchema,
  RegistrationFormSchema,
  VisitorFormSchema,
  type VisitorForm,
  type VisitorSubmit,
  type ProfileForm,
  type InactiveDeletionRequest,
  type PublicOrg,
  type PhotoUpload,
  type RegistrationForm,
} from '@escala/contracts';
import type { SelfProfile } from '@escala/domain/profileLink';
import type { RegistrationData } from '@escala/domain/types';

import { request } from '../http';

/**
 * Rotas sem login: "Encontre sua igreja" (o passo antes do login) e o formulário público de
 * cadastro pelo link da igreja.
 */

const PUBLIC = '/api/v1/public/orgs';
const id = encodeURIComponent;

export const publicApi = {
  /** Busca por nome, endereço ou cidade (2 letras ou mais). */
  searchOrgs: async (q: string): Promise<PublicOrg[]> =>
    (await request(PUBLIC, { query: { q }, schema: PublicOrgListSchema })).items,
  orgBySlug: (slug: string): Promise<PublicOrg> =>
    request(`${PUBLIC}/${id(slug)}`, { schema: PublicOrgSchema }),
  registrationForm: (slug: string, token: string): Promise<RegistrationForm> =>
    request(`${PUBLIC}/${id(slug)}/registration/${id(token)}`, { schema: RegistrationFormSchema }),
  /** O cadastro e a foto opcional (já recortada e reduzida, como a do perfil). */
  submitRegistration: (
    slug: string,
    token: string,
    data: RegistrationData,
    photo?: PhotoUpload | null
  ): Promise<unknown> =>
    request(`${PUBLIC}/${id(slug)}/registration/${id(token)}`, {
      method: 'POST',
      body: { data, ...(photo ? { photo } : {}), consent: true },
    }),
  /** A ficha de visitante pelo link (ou QR) da igreja. */
  visitorForm: (slug: string, token: string): Promise<VisitorForm> =>
    request(`${PUBLIC}/${id(slug)}/visitor/${id(token)}`, { schema: VisitorFormSchema }),
  submitVisitor: (slug: string, token: string, body: VisitorSubmit): Promise<unknown> =>
    request(`${PUBLIC}/${id(slug)}/visitor/${id(token)}`, { method: 'POST', body }),
  /** O link pessoal de completar o cadastro: o formulário e o salvar (vale na hora). */
  profileForm: (token: string): Promise<ProfileForm> =>
    request(`/api/v1/public/profile-links/${id(token)}`, { schema: ProfileFormSchema }),
  saveProfile: (token: string, profile: SelfProfile): Promise<unknown> =>
    request(`/api/v1/public/profile-links/${id(token)}`, { method: 'PUT', body: { profile, consent: true } }),
  /** "Excluir meus dados" de quem está inativo: o e-mail e a senha confirmam quem é. */
  requestInactiveDeletion: (body: InactiveDeletionRequest) =>
    request('/api/v1/privacy/inactive-requests', { method: 'POST', body, schema: PrivacyRequestSchema }),
};

export type PublicApi = typeof publicApi;
